/**
 * Sprint 166 — ¿Se puede recargar la pantalla AHORA sin que nadie pierda nada?
 *
 * La regla del Sprint 116 era "nunca recargar sola": se perdería el pedido a medio capturar. Sigue
 * siendo la regla; lo que cambia es que ahora se sabe cuándo NO hay nada que perder:
 *  - el carrito está vacío,
 *  - no hay ninguna ventana abierta (cobro, paquete, carga, alta…),
 *  - no hay un campo con algo escrito a medias,
 *  - no es el menú ni la página del pedido de un CLIENTE (su pedido vive sólo en su pantalla),
 *  - y nadie ha tocado nada en un minuto, o la pestaña ni siquiera está a la vista.
 */
export interface EstadoPantalla {
  articulosEnCarrito: number;
  hayVentanaAbierta: boolean;
  hayTextoAMedias: boolean;
  ruta: string;
  oculta: boolean;
  msDesdeUltimoToque: number;
}

export const QUIETO_MS = 60_000;

export function esMomentoSeguro(e: EstadoPantalla): boolean {
  if (e.articulosEnCarrito > 0) return false;
  if (e.hayVentanaAbierta) return false;
  if (e.hayTextoAMedias) return false;
  if (/^#\/(menu|pedido)(\/|\?|$)/.test(e.ruta)) return false;
  return e.oculta || e.msDesdeUltimoToque >= QUIETO_MS;
}

/** Lee la pantalla real (el navegador). */
/**
 * Auditoría `cyber` S166 (MEDIA): no basta con el campo que tiene el foco. La cajera escribe el conteo de
 * billetes, deja de tocar la pantalla para contar y el campo ya no tiene foco. Cualquier campo con algo
 * escrito cuenta como "a medias", salvo los buscadores. Es conservador a propósito: en una pantalla de
 * ajustes (campos llenos) simplemente no se recarga sola y queda la franja.
 */
function hayCamposConTexto(): boolean {
  const campos = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea');
  for (const c of campos) {
    if (c.disabled || c.readOnly) continue;
    if (c instanceof HTMLInputElement) {
      if (['hidden', 'checkbox', 'radio', 'button', 'submit', 'search', 'range', 'color'].includes(c.type)) continue;
      if (/buscar/i.test(c.placeholder)) continue;
    }
    if (c.value.trim() !== '' && c.value.trim() !== '0') return true;
  }
  return false;
}

export function leerPantalla(articulosEnCarrito: number, ultimoToque: number): EstadoPantalla {
  const activo = document.activeElement as HTMLElement | null;
  return {
    articulosEnCarrito,
    hayVentanaAbierta: !!document.querySelector('[aria-modal="true"], [role="dialog"], .fixed.inset-0'),
    hayTextoAMedias: hayCamposConTexto() || !!activo?.isContentEditable,
    ruta: window.location.hash,
    oculta: document.visibilityState === 'hidden',
    msDesdeUltimoToque: Date.now() - ultimoToque,
  };
}
